/**
 * The twenty-eight settings an organization can answer for, as rows.
 *
 * **Nothing here is invented.** The compartments are the desktop app's own
 * settings pages in its own order (`SECTIONS` plus the `Agents` group in
 * `AppSettingsView.tsx`), and every label, hint and option is the one in
 * `PanelsRuntime.tsx` / `PanelsNetwork.tsx`. A default an organization sets has
 * to be recognisable as the row a member would have set on their machine — if
 * it is worded differently it is a second thing to learn, not a default.
 *
 * Four things this shape reveals that a single `defaults` blob could not:
 *
 * 1. **Two of the twenty-six are not settings.** `gitEnabled` belongs to the
 *    GitHub integration and `tunnelsEnabled` to ngrok — *a tunnel runs as an
 *    account, and accounts are integrations*. A flat blob flattens that away.
 * 2. **Three rows are conditional**, and JSON cannot be. `dockerDiskGb` exists
 *    only when Docker is on; `agentPreviewLogins` and `agentProbes` only when
 *    Preview is. Today an organization can publish a Docker disk size to an
 *    organization that has no Docker.
 * 3. **`idleHintMs` is asked in seconds and stored in milliseconds.** In a
 *    textarea an administrator has to type `20000` and guess that the bounds
 *    are `5000–600000`.
 * 4. **The Dockerfile is not a value but a document**, so it takes the whole
 *    width and has no value column at all.
 *
 * And the model and the effort are **one** row. What has to be valid is the
 * pair — `contracts/settings/models.ts` stops GPT-5.5 and GPT-5.2 at `xhigh` —
 * and validating the two separately admits combinations no session can be
 * launched with. So the pair is what is picked.
 */
import { EFFORTS, MODELS, MODEL_EFFORT_OPTIONS } from '@airlock/contracts/settings/models';
import { jiraHost } from '@airlock/contracts/settings/primitives';

export type SettingKind = 'bool' | 'oneOf' | 'int' | 'text' | 'wide' | 'modelEffort' | 'agents' | 'extensions' | 'autoLand' | 'audit';

/**
 * Whether a row is a **document** rather than a value: it has no value column
 * at all and takes the whole width under its own line.
 *
 * Two of them now — the session image, and what a session's agent is given
 * (§5.26) — which is why it is a predicate rather than a comparison with
 * `'wide'` written out in four places on two pages.
 */
export const isDocumentRow = (kind: SettingKind): boolean =>
  kind === 'wide' || kind === 'extensions' || kind === 'autoLand' || kind === 'audit';

export interface SettingSpec {
  /** The key in `defaults`. `modelEffort` writes `defaultModel` too. */
  key: string;
  label: string;
  kind: SettingKind;
  /**
   * Which half of the document it belongs to.
   *
   * `defaults` is what an organization prefers, laid over each member's own
   * settings for its workspaces. `onboarding` is what a new install is handed
   * **once**, when it joins — a member may change it on their machine
   * afterwards and that change stays there. They are two different promises,
   * which is why the row says which one it is making.
   */
  layer?: 'defaults' | 'onboarding';
  hint?: string;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  step?: number;
  /** What the bounds are counted in, written beside them. */
  unit?: string;
  /** Asked in one unit and stored in another — seconds on screen, ms in the document. */
  scale?: number;
  placeholder?: string;
  /**
   * The key that has to be on for this row to mean anything. `held`, on a
   * text row, holds its field while the key is off and says why (item 89c).
   */
  needs?: { key: string; value: unknown; note: string; held?: string };
  /**
   * A text row's own test of what is typed, with the sentence said under the
   * field when it fails (item 89b) — the same test the Worker refuses by, so
   * the refusal is read before Publish rather than after it.
   */
  check?: { accepts: (value: string) => boolean; refused: string };
}

export interface SettingGroup {
  /** A heading inside a section, as the desktop page has it. Optional. */
  label?: string;
  rows: SettingSpec[];
}

export interface SettingsSectionSpec {
  key: string;
  /** What the page says under its title. */
  lede: string;
  /** Notes the desktop page carries, which are as much of the answer as the rows. */
  notes?: string[];
  groups: SettingGroup[];
  /**
   * One agent's own configuration document, which is not a set of rows.
   *
   * The catalogue of what each CLI accepts is the app's, and it is large: it is
   * the one place here that is still edited as the document the app accepts,
   * rather than as typed rows. That is not the JSON this redesign set out to
   * remove — the twenty-six settings were. `mcp_servers`, `hooks` and `env` are
   * documents whose keys the person chooses, and the catalogue calls them
   * opaque for the same reason.
   */
  agentDocument?: string;
}

/**
 * The pair grammar is `contracts`' own — the app's settings offer the same one
 * field, so a second copy here would be two lists of pairs drifting apart on
 * the two surfaces that write the same document.
 */
export { MODEL_EFFORT_OPTIONS, pairOf, splitPair } from '@airlock/contracts/settings/models';

/** The effort labels, for showing an inherited pair in words. */
export const EFFORT_LABEL: Record<string, string> = Object.fromEntries(EFFORTS.map((one) => [one.value, one.label]));
export const MODEL_LABEL: Record<string, string> = Object.fromEntries(MODELS.map((one) => [one.value, one.label]));

/* ── the six sections ───────────────────────────────────────────────────── */

export const SESSIONS: SettingsSectionSpec = {
  key: 'sessions',
  lede: 'What the new-session form starts with. A workspace may override each of these, and a member may override it again — unless it is locked.',
  groups: [
    {
      rows: [
        {
          key: 'defaultModel',
          label: 'Sessions start on',
          kind: 'modelEffort',
          options: MODEL_EFFORT_OPTIONS,
          hint: 'One field and not two: what has to be valid is the pair, so the pair is what is picked: GPT-5.5 and GPT-5.2 stop at X-High.'
        },
        {
          key: 'defaultSourceBranch',
          label: 'Source branch',
          kind: 'text',
          placeholder: 'main',
          hint: 'The branch a session is cut from, and the fallback when a repository has no such branch.'
        },
        {
          key: 'archiveRetentionDays',
          label: 'Keep archived tasks for',
          kind: 'int',
          min: 1,
          max: 365,
          unit: 'days',
          hint: 'A task is archived when its work is merged, with a summary of the run. After this many days it is deleted.'
        }
      ]
    }
  ]
};

export const SANDBOX: SettingsSectionSpec = {
  key: 'sandbox',
  lede: 'Where a session’s copy of the workspace runs. A value left inherited does not enter the document: the install keeps its own.',
  notes: [
    'This is the answer a new session starts from, not a placement: a session is placed once, the choice is written on it, and a live sandbox keeps the backend that created it.'
  ],
  groups: [
    {
      rows: [
        {
          key: 'sandboxBackend',
          label: 'Backend',
          kind: 'oneOf',
          options: [
            { value: 'auto', label: 'Micro-VM when available (recommended)' },
            { value: 'local', label: 'Local — no isolation' }
          ],
          hint: 'A micro-VM gives each session its own kernel, a non-root user and a deny-by-default egress policy. Local gives it a copied tree and HOME, and is not a security boundary.'
        },
        {
          key: 'dockerfile',
          label: 'Session image',
          kind: 'wide',
          hint: 'Airlock boots the FROM image and replays these instructions once, into a snapshot every session then starts from. Micro-VM sessions only.'
        }
      ]
    },
    {
      label: 'Container runtime',
      rows: [
        {
          key: 'containerRuntime',
          label: 'Docker',
          kind: 'oneOf',
          options: [
            { value: 'none', label: 'Off' },
            { value: 'docker', label: 'On, inside the VM' }
          ],
          hint: 'A daemon inside the session’s own micro-VM, for whatever the work needs containers for. Airlock gives it a separate disk and never exposes the host Docker socket.'
        },
        {
          key: 'dockerDiskGb',
          label: 'Docker disk (GB)',
          kind: 'int',
          min: 5,
          max: 500,
          unit: 'GB',
          hint: 'Per-session storage for its images, containers and build cache. Removed with the session.',
          needs: {
            key: 'containerRuntime',
            value: 'docker',
            note: 'Only exists where Docker is on — the app hides this row otherwise, and a document that carries it hands a disk size to an organization that has no Docker.'
          }
        }
      ]
    },
    {
      label: 'VM resources',
      rows: [
        { key: 'vmCpu', label: 'CPU cores', kind: 'int', min: 1, max: 16 },
        { key: 'vmMemoryMb', label: 'Memory (MB)', kind: 'int', min: 1024, max: 65536, step: 512, unit: 'MB' },
        { key: 'vmDiskGb', label: 'Disk (GB)', kind: 'int', min: 5, max: 500, unit: 'GB' }
      ]
    }
  ]
};

export const NETWORK: SettingsSectionSpec = {
  key: 'network',
  lede: 'A sandbox reaches the hosts its allowlist names and nothing else. What an organization answers for here holds on every member’s machine, for every workspace on it — the organization’s and their own.',
  notes: [
    'A micro-VM session refuses a host nobody listed at its own network. With Ask or Allow, the member is asked about it, and allowing it adds it to that workspace and restarts the session on a machine that reaches it; with Block nobody is asked and the allowlist is the whole answer. A workspace’s first install is the one run where Ask holds the call itself. Locked, the mode holds on every workspace a member has.',
    'The two lists below are not settings a machine starts from: allowed hosts are added everywhere, and blocked hosts are refused everywhere — a member cannot allow one, and is never asked about one.'
  ],
  groups: [
    {
      rows: [
        {
          key: 'egressMode',
          label: 'When a session reaches a host nobody listed',
          kind: 'oneOf',
          options: [
            { value: 'ask', label: 'Ask, holding the call' },
            { value: 'auto', label: 'Allow automatically' },
            { value: 'off', label: 'Block — allowlist only' }
          ],
          hint: 'Asking holds the connection until somebody answers, so the call itself still succeeds.'
        }
      ]
    }
  ]
};

export const GIT: SettingsSectionSpec = {
  key: 'git',
  lede: 'Merges are made on the host, by a person. These are what a session does around one.',
  notes: [
    'Host git always runs with hooks disabled, and each merge lands as one commit on the target branch. Starting a session from a pull request is GitHub’s — under Integrations.'
  ],
  groups: [
    {
      rows: [
        {
          key: 'commitCoAuthoredBy',
          label: 'Credit the agent in the commit',
          kind: 'bool',
          hint: 'The commit is always authored with the member’s own host git identity; this appends a Co-authored-by trailer naming the agent that did the work.'
        },
        {
          key: 'autoSync',
          label: 'Sync when the agent hands back',
          kind: 'bool',
          hint: 'When the agent finishes a turn and the branch the session was cut from has moved, the session is rebased onto it. A sync that would stop on conflicts is abandoned at once.'
        },
        {
          key: 'autoLand',
          label: 'When a session is finished',
          kind: 'autoLand',
          hint: 'Once the agent has handed back and nothing is left to run, what happens without the member: the branch, the commit and merge, the push, the pull request, then the session. Each step stops the run on the first refusal and says why; what landed stays landed.'
        }
      ]
    }
  ]
};

export const AUDIT: SettingsSectionSpec = {
  key: 'audit',
  lede: 'What a change is checked against while the agent writes it, and what the Change Map is drawn with.',
  notes: [
    'Nothing of the audit runs in a session that reviews someone’s pull request: there, the map’s findings are leads for the reviewing agent’s draft comments.'
  ],
  groups: [
    {
      rows: [
        {
          key: 'audit',
          label: 'Each hand-back',
          kind: 'audit',
          hint: 'When the agent hands back, the team’s Rules read its change, and what they and the Change Map found goes back to the agent to fix or dismiss, each with its reason — a turn of its own when there is something to settle. Off leaves both to the member’s Audit and Send. Automatic draws the map on Sonnet at low effort, Terra where Codex is the only agent. What holds a merge is chosen here too: the rules broken of which levels, the findings left unsettled of which severities.'
        }
      ]
    }
  ]
};

export const NOTIFICATIONS: SettingsSectionSpec = {
  key: 'notifications',
  lede: 'A session runs while somebody does something else. This is how it tells them it stopped.',
  notes: [
    'Nothing is shown while the Airlock window has focus, in any mode. Repeats about the same session are held for a minute, so a status that flickers cannot produce a run of banners.'
  ],
  groups: [
    {
      rows: [
        {
          key: 'notifications',
          label: 'Interrupt me when a session',
          kind: 'oneOf',
          options: [
            { value: 'all', label: 'Finished, and anything stopped' },
            { value: 'attention', label: 'Only what is stopped' },
            { value: 'off', label: 'Never' }
          ],
          hint: 'Stopped is an agent waiting on an answer, a workflow gate, a held connection, or a failure. Finished is the handback.'
        }
      ]
    }
  ]
};

export const LOGS: SettingsSectionSpec = {
  key: 'logs',
  lede: 'Project process output is tailed from the sandbox into files on the member’s machine.',
  groups: [
    {
      rows: [
        {
          key: 'logRetentionMb',
          label: 'Retention per log file (MB)',
          kind: 'int',
          min: 16,
          max: 8192,
          step: 16,
          unit: 'MB',
          hint: 'A file past this size is truncated on the next write.'
        },
        {
          key: 'logBufferLines',
          label: 'Lines kept in the logs dock',
          kind: 'int',
          min: 200,
          max: 50000,
          step: 100,
          unit: 'lines',
          hint: 'Per project. The files and their full-text index keep everything either way.'
        }
      ]
    }
  ]
};

/** The `All agents` page — what applies whichever agent a session runs. */
export const ALL_AGENTS: SettingsSectionSpec = {
  key: 'all',
  lede: 'What applies to every agent, whichever one a session runs. Each agent has a page of its own for its switch and its own settings.',
  notes: [
    'A Claude subscription token is never copied into a micro-VM — it is substituted into the TLS handshake host-side. So signing in to each agent is the one thing a new member still does themselves, whatever an organization sets here.'
  ],
  groups: [
    {
      label: 'How it runs',
      rows: [
        {
          key: 'autonomous',
          label: 'Run autonomously',
          kind: 'bool',
          hint: 'In a micro-VM the agent is launched with every approval skipped and its own sandbox off: the boundary is the VM, not the prompt. Off, it asks for everything.'
        },
        {
          key: 'privacyMask',
          label: 'Mask personal data',
          kind: 'bool',
          hint: 'Emails, phone numbers, IBANs, card numbers, NIRs, public IPs, people’s names, postal addresses and each member’s listed names replaced by placeholders in what Airlock sends the agent — prompts, tasks, tickets, notes, step prompts and check output. The agent still reads the repositories’ files as they are.'
        },
        {
          key: 'agentConfigSource',
          label: 'Reuse the agent’s host config',
          kind: 'oneOf',
          options: [
            { value: 'host', label: 'Copy and sanitize it' },
            { value: 'none', label: 'Don’t reuse it' }
          ],
          hint: 'From that agent’s own directory — ~/.claude, ~/.codex. Credentials and host paths are stripped on the way in.'
        },
        {
          key: 'agentUpdate',
          label: 'Agent version',
          kind: 'oneOf',
          options: [
            { value: 'latest', label: 'Update to the newest release on start' },
            { value: 'pin', label: 'Keep the release in the image' }
          ],
          hint: 'A session image is a snapshot, so it keeps the release it was built with until the Dockerfile changes.'
        },
        {
          key: 'agentExtensions',
          label: 'Skills, MCP & plugins',
          kind: 'extensions',
          hint: 'What a session’s agent can do beyond what its CLI ships with: folders of the workspace given to it as skills, the tool servers it may call, the plugins it loads. Each enabled row becomes a line of the agent’s own configuration inside the session.'
        },
        {
          key: 'idleHintMs',
          label: 'Idle fallback (seconds)',
          kind: 'int',
          min: 5,
          max: 600,
          unit: 'seconds',
          // Asked in seconds and kept in milliseconds, exactly as the app's own
          // field does it. In a textarea this was 20000 and a guess at the range.
          scale: 1000,
          hint: 'Some prompts emit no hook event. After this long without one, a working session is shown as waiting for input.'
        }
      ]
    },
    {
      label: 'Preview',
      rows: [
        {
          key: 'agentPreview',
          label: 'Let the agent drive Preview',
          kind: 'bool',
          hint: 'Gives the agent tools to open the pages its session serves, click through them, screenshot them and read what they logged — so it can check its own work instead of reporting that it did.'
        },
        {
          key: 'agentPreviewLogins',
          label: '…including saved logins',
          kind: 'bool',
          hint: 'Lets it ask the host to sign a page in with a login from the workspace vault. The password is typed host-side and never enters the sandbox.',
          needs: {
            key: 'agentPreview',
            value: true,
            note: 'Only exists where Preview is on — the app hides this row otherwise.'
          }
        },
        {
          key: 'agentProbes',
          label: 'Live probes',
          kind: 'oneOf',
          options: [
            { value: 'off', label: 'Off' },
            { value: 'attach', label: 'Attach to what runs — Node and the page' },
            { value: 'ready', label: 'Also start Java, Python and Go under a debugger' }
          ],
          hint: 'Lets the agent watch one line of a running project without editing or restarting it, and records every value it captured under the session.',
          needs: {
            key: 'agentPreview',
            value: true,
            note: 'Only exists where Preview is on — the app hides this row otherwise.'
          }
        }
      ]
    }
  ]
};

export const SECTION_SPECS: Record<string, SettingsSectionSpec> = {
  sessions: SESSIONS,
  sandbox: SANDBOX,
  network: NETWORK,
  git: GIT,
  audit: AUDIT,
  notifications: NOTIFICATIONS,
  logs: LOGS
};

/* ── the agents, and the accounts an organization connects ──────────────── */

/**
 * Which agents a member may launch. An onboarding value, not a default: it is
 * handed to an install once, and `enabledAgents` will not let it be empty —
 * an organization that offers no agent offers no product.
 */
export const AGENTS_ROW: SettingSpec = {
  key: 'agents',
  label: 'Agents a member may launch',
  kind: 'agents',
  layer: 'onboarding',
  options: [
    { value: 'claude-code', label: 'Claude Code' },
    { value: 'codex', label: 'Codex' }
  ],
  hint: 'Handed to an install when it joins. Locked, it is the only agents a member’s runs may use: a task, a workflow step or an agent naming another is refused before it starts. Signing in to each one is still the member’s own act — a subscription token is never copied into a sandbox.'
};

export const JIRA: SettingsSectionSpec = {
  key: 'jira',
  lede: 'The Jira site this organization reads tickets from. Connect it once here; each workspace names which boards feed it, and each member signs in with their own credential.',
  groups: [
    {
      rows: [
        {
          key: 'jiraEnabled',
          label: 'Jira',
          kind: 'bool',
          layer: 'onboarding',
          hint: 'Off means off: nothing is fetched, no token is read, and the surfaces that would offer it are absent. Locked off, a member’s Airlock does not list it at all.'
        },
        {
          key: 'jiraHost',
          label: 'Site',
          kind: 'text',
          layer: 'onboarding',
          placeholder: 'acme.atlassian.net',
          hint: 'The bare host, with no scheme, port or path — a URL is refused rather than quietly rewritten.',
          needs: {
            key: 'jiraEnabled',
            value: true,
            note: 'Only means anything while Jira is on.',
            held: 'Turn on Jira first'
          },
          // The organization's document keeps a site only as jiraHost() would
          // write it (parseOrgSettings compares the two), so anything it would
          // reduce — a scheme, a port, a path, capitals — is refused.
          check: {
            accepts: (value) => value === '' || jiraHost(value) === value,
            refused: 'Enter the bare host, like acme.atlassian.net — no scheme, port or path.'
          }
        },
        {
          key: 'taskSyncMinutes',
          label: 'Check for changes every',
          kind: 'int',
          layer: 'onboarding',
          min: 2,
          max: 120,
          unit: 'minutes',
          hint: 'How often an install asks the tracker what moved. Lanes and statuses travel both ways, and the later move wins.'
        }
      ]
    }
  ]
};

export const TRELLO: SettingsSectionSpec = {
  key: 'trello',
  lede: 'Trello boards, read the same way Jira’s are. Each member signs in with their own credential.',
  groups: [
    {
      rows: [
        {
          key: 'trelloEnabled',
          label: 'Trello',
          kind: 'bool',
          layer: 'onboarding',
          hint: 'Off means off: nothing is fetched, and the surfaces that would offer it are absent. Locked off, a member’s Airlock does not list it at all.'
        }
      ]
    }
  ]
};

export const GITHUB: SettingsSectionSpec = {
  key: 'github',
  lede: 'Airlock signs in to GitHub with its own token, kept under ~/.airlock and never given to a sandbox. A member’s own gh, its config and their git config are never touched.',
  notes: [
    'The account itself is connected on each machine — a sign-in is a person’s, not an organization’s. What an organization answers for is whether the surfaces exist at all.',
    'Or the organization connects Airlock’s GitHub app below: members’ machines then push and open pull requests with a short-lived token for each workspace’s repositories, and fall back to their own sign-in where it does not reach.'
  ],
  groups: [
    {
      rows: [
        {
          key: 'gitEnabled',
          // The integration is **GitHub**. `Pull request integration` named
          // what it is used for, which put a fifth name in a list of four
          // providers and made the row read as something other than the row on
          // the rail above it. What it does belongs in the hint.
          label: 'GitHub',
          kind: 'bool',
          hint: 'Start a session from a pull request, and push its merge back onto that pull request’s branch. Off means nothing is downloaded, no provider token is read, and the surfaces that would offer it are absent — and locked off, a member’s Airlock does not list it at all.'
        }
      ]
    }
  ]
};

export const NGROK: SettingsSectionSpec = {
  key: 'ngrok',
  lede: 'A tunnel runs as an account, and accounts are integrations — which is why this is here and not among the settings. The authtoken itself lives in each machine’s keychain.',
  groups: [
    {
      rows: [
        {
          key: 'tunnelsEnabled',
          label: 'Public URLs',
          kind: 'bool',
          hint: 'Lets a session publish a port at a public address. Off by default, unlike Preview: this one reaches out of the machine. Locked off, a member’s Airlock does not list it at all.'
        }
      ]
    }
  ]
};

/**
 * The bookmarklet, which is the one integration with no account behind it.
 *
 * So it is the one row this section can hold, and the page is one switch. There
 * is nothing to sign in to, no site to name and no interval to set: a page in
 * somebody's own browser reaches Airlock as a task, and the only decision an
 * organization has is whether that is offered at all.
 */
export const BOOKMARKLET: SettingsSectionSpec = {
  key: 'bookmarklet',
  lede: 'A ticket, an error page or a log open in a browser becomes a task in one click — signed in, rendered, SSO and all, because it is the member’s own browser that reads it.',
  notes: [
    'Nothing leaves the machine and nothing is signed in to: the bookmark is the browser’s, and what it sends goes straight to the member’s own Airlock. The only thing to decide here is whether it is offered.'
  ],
  groups: [
    {
      rows: [
        {
          key: 'bookmarkletEnabled',
          label: 'Bookmarklet',
          kind: 'bool',
          layer: 'onboarding',
          hint: 'Off and locked means it is absent on a member’s machine — not listed, not in their catalogue, nothing to set up.'
        }
      ]
    }
  ]
};

export const ADD_INTEGRATION: SettingsSectionSpec = {
  key: 'add',
  lede: 'An integration is an account Airlock acts as — with one exception, the bookmarklet, which is a link and no account at all. Turning one on here is what puts it on the rail beside this page; an agent’s own sign-in stays each member’s.',
  notes: [
    'Off is a real off, and locking it is how an organization says it does not support one: the member’s Airlock stops listing it entirely rather than offering it and refusing. Unlocked, off is only what this organization starts a new install with — a member can still turn it on for themselves.'
  ],
  // The switches again, all five on one page. Not a duplicate of the pages
  // above but the way back to them: an integration that is off has no row on
  // the rail, so without this there would be no door to the page that turns it
  // on. One document either way — a switch flicked here is the same key.
  groups: [
    {
      rows: [
        { ...JIRA.groups[0].rows[0], hint: 'Tickets assigned to a member arrive in their Backlog. The site is named on Jira’s own page.' },
        { ...TRELLO.groups[0].rows[0], hint: 'Cards a member is on arrive in their Backlog, the same way Jira’s tickets do.' },
        { ...GITHUB.groups[0].rows[0], hint: 'Start a session from a pull request, and push its merge back onto that branch.' },
        // Named for the integration here and for what it does on its own page:
        // this list answers "which of these do we support", and `Public URLs`
        // is not one of the five things the rail beside it names.
        { ...NGROK.groups[0].rows[0], label: 'ngrok', hint: 'A public URL onto a port a session serves, for a webhook or a sign-in callback.' },
        { ...BOOKMARKLET.groups[0].rows[0], hint: 'A page open in a browser becomes a task in one click. No account, nothing to set up.' }
      ]
    }
  ]
};

/**
 * Whether members' installs send their usage statistics here (the Statistics
 * page). An onboarding value like the agents: off — or unset — the installs
 * keep their days to themselves; on, they send the days from then on; held,
 * a member cannot turn it the other way.
 */
export const USAGE_ROW: SettingSpec = {
  key: 'usageStatsEnabled',
  label: 'Usage statistics',
  kind: 'bool',
  layer: 'onboarding',
  hint: 'On, each member’s Airlock sends a row a day of what its agents spent and what came of it, from the day this is turned on. Hold it to keep members from opting out — or, held off, from collecting at all.'
};

/**
 * Whether members' installs send a record of each run in the organization's
 * workspaces (the Runs page). Off by default on both sides: an install records
 * nothing until the organization publishes this on, and its onboarding then
 * hands it to every member; held, nobody opts out. The record is counts,
 * names and domains, never a prompt or a line of code.
 */
export const RUN_TRAIL_ROW: SettingSpec = {
  key: 'runTrailEnabled',
  label: 'Trail of runs',
  kind: 'bool',
  layer: 'onboarding',
  hint: 'On, each member’s Airlock sends a record of every run in this organization’s workspaces: agent, model, outcome, counts, refused hosts, pull requests and estimated cost — never a prompt, code or a secret. Members are told in the app. Hold it to keep members from opting out.'
};

export const AGENT_SECTION_SPECS: Record<string, SettingsSectionSpec> = {
  all: { ...ALL_AGENTS, groups: [{ rows: [AGENTS_ROW] }, ...ALL_AGENTS.groups, { rows: [USAGE_ROW, RUN_TRAIL_ROW] }] },
  'claude-code': {
    key: 'claude-code',
    lede: 'The whole of Claude Code’s configuration, as the app accepts it — handed to an install when it joins and written into its config inside each session. A member may change any of it on their own machine afterwards, and that change stays there.',
    groups: [],
    agentDocument: 'claude-code'
  },
  codex: {
    key: 'codex',
    lede: 'The whole of Codex’s configuration, as the app accepts it — handed to an install when it joins and written into its config inside each session. A member may change any of it on their own machine afterwards, and that change stays there.',
    groups: [],
    agentDocument: 'codex'
  }
};

export const INTEGRATION_SECTION_SPECS: Record<string, SettingsSectionSpec> = {
  jira: JIRA,
  trello: TRELLO,
  github: GITHUB,
  ngrok: NGROK,
  bookmarklet: BOOKMARKLET,
  add: ADD_INTEGRATION
};
